import { useMemo } from "react";
import { useQuery } from "react-query";
import dashboardService from "services/DashBoardService";

const EMPTY_MONTHLY_DATA = Array.from({ length: 12 }, (_, i) => ({
  month: `0000-${String(i + 1).padStart(2, "0")}`,
  total_completed: 0,
  success_count: 0,
  fail_count: 0,
  success_percent: 0,
  fail_percent: 0,
  lead_time_hours: 0,
}));

export function useKpiPerformanceStats({ year, baseParams, enabled }) {
  const queryParams = useMemo(() => {
    if (!baseParams || typeof baseParams !== "object") {
      return null;
    }

    return {
      ...baseParams,
      from_time: `${year}-01-01`,
      to_time: `${year}-12-31`,
    };
  }, [baseParams, year]);

  const { data: rawResponse, isFetching, isLoading } = useQuery(
    ["kpi-performance-stats", year, baseParams],
    () => dashboardService.statsByKpiPerformance(/** @type {Record<string, string>} */(queryParams)),
    {
      enabled: Boolean(enabled && queryParams),
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const apiBody = rawResponse?.data;
  const rawData = useMemo(() => {
    if (Array.isArray(apiBody)) {
      return apiBody;
    }
    if (Array.isArray(apiBody?.data)) {
      return apiBody.data;
    }
    return [];
  }, [apiBody]);
  const filterApplied = apiBody && typeof apiBody === "object" && !Array.isArray(apiBody)
    ? apiBody.filter_applied ?? {}
    : {};
  const targetSuccessPercent = filterApplied.target_success_percent ?? 92;

  // Luôn trả đủ 12 tháng trong năm; tháng thiếu API thì điền 0.
  const monthlyData = useMemo(() => {
    if (!rawData.length) return EMPTY_MONTHLY_DATA.map(item => ({ ...item, month: `${year}-${item.month.slice(5)}` }));
    const monthlyDataByKey = new Map(
      rawData.map(item => {
        const monthNumber = Number(item?.month);
        const normalizedMonth = Number.isFinite(monthNumber) && monthNumber >= 1 && monthNumber <= 12
          ? `${year}-${String(monthNumber).padStart(2, "0")}`
          : String(item?.month || "");
        return [normalizedMonth, item];
      })
    );
    return Array.from({ length: 12 }, (_, i) => {
      const monthStr = `${year}-${String(i + 1).padStart(2, "0")}`;
      const found = monthlyDataByKey.get(monthStr);
      return found ?? {
        month: monthStr,
        total_completed: 0,
        success_count: 0,
        fail_count: 0,
        success_percent: 0,
        fail_percent: 0,
        lead_time_hours: 0,
      };
    });
  }, [rawData, year]);

  return {
    monthlyData,
    targetSuccessPercent,
    isFetching,
    isLoading,
  };
}
